import {
	ComputedOptions,
	ReactionOptions,
	ReactiveValueOptions,
} from "./reactive-primitive-types";

/**
 * A special symbol used to indicate that a reactive value has changed,
 * bypassing the standard equality check. This is useful in scenarios where
 * the content of the reactive values is not needed, but a change notification
 * is still desired. Assigning `CHANGED` as the content of the reactive value
 * will always result in change propagation. This, however, only applies
 * to the default equality check. If the equality check is overwritten,
 * `CHANGED` may need to be accounted for in the custom equality check
 */
export const CHANGED = Symbol();

export const reactiveValueEquals = (oldValue: unknown, newValue: unknown) => {
	if (defaultReactiveValueOptions.equals) {
		return defaultReactiveValueOptions.equals(oldValue, newValue);
	}
	return defaultEquals(oldValue, newValue);
};

export const defaultEquals = (oldValue: unknown, newValue: unknown) => {
	return newValue !== CHANGED && Object.is(oldValue, newValue);
};

// Reactive Value Options

export const defaultReactiveValueOptions: ReactiveValueOptions = {
	equals: defaultEquals,
};

/**
 * Retrieves a copy of the default options used for reactive values.
 * This ensures that modifications to the returned options object do not affect
 * the global default settings.
 *
 * @returns A new object containing the current default `ReactiveValueOptions`.
 */
export const getDefaultReactiveValueOptions = (): ReactiveValueOptions => {
	return {
		...defaultReactiveValueOptions,
	};
};

/**
 * Configures the global default options for reactive values.
 * These defaults will be used by newly created reactive values unless overridden
 * by options provided at creation time.
 *
 * @param newOptions - An object containing partial `ReactiveValueOptions` to update.
 *   Only the specified properties will be updated; unspecified properties will retain
 *   their current default values. If a property is set to `undefined`, it will revert
 *   to its original default (e.g., `defaultEquals` for `equals`).
 */
export const configureDefaultReactiveValueOptions = (
	newOptions: Partial<ReactiveValueOptions>
) => {
	for (const p in newOptions) {
		switch (p) {
			case "equals":
				defaultReactiveValueOptions[p] = newOptions[p] ?? defaultEquals;
				break;
		}
	}
};

// Computed Options

export const defaultComputedOptions: ComputedOptions = {
	equals: defaultEquals,
};

/**
 * Retrieves a copy of the default options used for computed atoms.
 * This ensures that modifications to the returned options object do not affect
 * the global default settings.
 *
 * @returns A new object containing the current default `ComputedOptions`.
 */
export const getDefaultComputedOptions = (): ComputedOptions => {
	return {
		...defaultComputedOptions,
	};
};

/**
 * Configures the global default options for computed atoms.
 * These defaults will be used by newly created computed atoms unless overridden
 * by options provided at creation time.
 *
 * @param newOptions - An object containing partial `ComputedOptions` to update.
 *   Only the specified properties will be updated; unspecified properties will retain
 *   their current default values. If a property is set to `undefined`, it will revert
 *   to its original default (e.g., `defaultEquals` for `equals`).
 */
export const configureDefaultComputedOptions = (
	newOptions: Partial<ComputedOptions>
) => {
	for (const p in newOptions) {
		switch (p) {
			case "equals":
				defaultComputedOptions[p] = newOptions[p] ?? defaultEquals;
				break;
		}
	}
};

// Reaction Options

export const defaultReactionScheduler: (fn: () => void) => void = (fn) => fn();

export const defaultReactionOptions: ReactionOptions = {
	scheduler: defaultReactionScheduler,
};

/**
 * Retrieves a copy of the default options used for reactions.
 * This ensures that modifications to the returned options object do not affect
 * the global default settings.
 *
 * @returns A new object containing the current default `ReactionOptions`.
 */
export const getDefaultReactionOptions = (): ReactionOptions => {
	return {
		...defaultReactionOptions,
	};
};

/**
 * Configures the global default options for reactions.
 * These defaults will be used by newly created reactions unless overridden
 * by options provided at creation time.
 *
 * @param newOptions - An object containing partial `ReactionOptions` to update.
 *   Only the specified properties will be updated; unspecified properties will retain
 *   their current default values. If a property is set to `undefined`, it will revert
 *   to its original default (e.g., `defaultReactionScheduler` for `scheduler`).
 */
export const configureDefaultReactionOptions = (
	newOptions: Partial<ReactionOptions>
) => {
	for (const p in newOptions) {
		switch (p) {
			case "scheduler":
				defaultReactionOptions[p] = newOptions[p] ?? defaultReactionScheduler;
				break;
		}
	}
};
